/**
 * TokDraw — app bootstrap and UI wiring.
 * State lives in ./state.js; pure logic in ./lib; the draw itself in ./draw/engine.js.
 */
import '@fontsource-variable/inter';
import '@fontsource-variable/outfit';
import '@fontsource/noto-sans-myanmar/400.css';
import '@fontsource/noto-sans-myanmar/600.css';
import '@fontsource/noto-sans-myanmar/700.css';
import './styles.css';

import { ApiError, loadComments } from './api.js';
import { downloadCertificate } from './certificate.js';
import { countdownSeconds, createCountdownView } from './draw/countdown.js';
import { createDrawEngine, drawStatus, isGiveawayComplete } from './draw/engine.js';
import { createSlotView } from './draw/slot.js';
import { createWheelView } from './draw/wheel.js';
import { celebrate, onCelebrate } from './fx/confetti.js';
import { recordingAudioTrack, setMuted, unlockAudio } from './fx/audio.js';
import { creatorSubtitle, creatorTitle, resolveCreator } from './lib/creator.js';
import { winnersToCsv } from './lib/csv.js';
import { buildEntries, countPeople, eligibleEntries, winnerKeys } from './lib/entries.js';
import { introScene, winnersScene } from './lib/recap-scene.js';
import { parseStep, resolveStep, stepHash } from './lib/steps.js';
import { MAX_QUANTITY, activeWinners, clampQuantity, createTier, minQuantity, moveTier, nextOpenTierId, tierFilled } from './lib/tiers.js';
import { createPresentation } from './presentation.js';
import { createCompositor } from './recap/compositor.js';
import * as recorder from './recorder.js';
import { store } from './state.js';
import { createAutoScroll } from './ui/autoscroll.js';
import { $, $$, avatarNode, clock, downloadBlob, el, fmt, slug, themeVar, timestamp } from './ui/dom.js';
import { hydrateIcons, icon, setIcon } from './ui/icons.js';
import { toast } from './ui/toast.js';

/* ------------------------------------------------------------------ */
/* Elements                                                            */
/* ------------------------------------------------------------------ */
const ui = {
  themeSelect: $('#theme-select'),
  muteBtn: $('#mute-btn'),
  exitPresentBtn: $('#exit-present-btn'),
  loadForm: $('#load-form'),
  urlInput: $('#video-url'),
  loadBtn: $('#load-btn'),
  demoBtn: $('#demo-btn'),
  loadStatus: $('#load-status'),
  steps: {
    links: $$('[data-step-link]'),
    pages: $$('[data-step-page]'),
    continueVideoBtn: $('#continue-video-btn'),
    setupNextBtn: $('#setup-next-btn'),
    setupHint: $('#setup-hint'),
    drawResultsBtn: $('#draw-results-btn'),
    resultsSummary: $('#results-summary'),
    newGiveawayBtn: $('#new-giveaway-btn'),
  },
  page: {
    avatar: $('#page-avatar'),
    title: $('#page-title'),
    sub: $('#page-sub'),
    participantsCount: $('#participants-count'),
    participantList: $('#participant-list'),
    participantEmpty: $('#participant-empty'),
    startBtn: $('#present-start-btn'),
    winnersCount: $('#present-winners-count'),
    winnersList: $('#present-winners-list'),
  },
  stageCreator: {
    avatar: $('#stage-creator-avatar'),
    title: $('#stage-creator-title'),
  },
  keywordInput: $('#keyword-input'),
  excludeInput: $('#exclude-input'),
  uniqueInput: $('#unique-input'),
  stats: {
    comments: $('#stat-comments'),
    entries: $('#stat-entries'),
    people: $('#stat-people'),
    eligible: $('#stat-eligible'),
  },
  tierEditor: $('#tier-editor'),
  addTierBtn: $('#add-tier-btn'),
  dashboard: $('#tier-dashboard'),
  nowTier: $('#now-tier'),
  nowPrize: $('#now-prize'),
  viewport: $('#stage-viewport'),
  modeButtons: $$('.seg-btn[data-mode]'),
  durationInput: $('#duration-input'),
  durationOutput: $('#duration-output'),
  drawBtn: $('#draw-btn'),
  drawBtnLabel: $('#draw-btn-label'),
  viewWinnersBtn: $('#view-winners-btn'),
  drawHint: $('#draw-hint'),
  reveal: $('#reveal'),
  winnersList: $('#winners-list'),
  winnersEmpty: $('#winners-empty'),
  exportCsvBtn: $('#export-csv-btn'),
  clearWinnersBtn: $('#clear-winners-btn'),
  recordingsList: $('#recordings-list'),
  recordingsEmpty: $('#recordings-empty'),
  recordingSaving: $('#recording-saving'),
  stopShareBtn: $('#stop-share-btn'),
  resetAllBtn: $('#reset-all-btn'),
};

hydrateIcons();

/* ------------------------------------------------------------------ */
/* Derived data                                                        */
/* ------------------------------------------------------------------ */
let entriesMemo = { comments: null, filters: null, entries: [] };
function getEntries(state) {
  if (state.comments !== entriesMemo.comments || state.filters !== entriesMemo.filters) {
    entriesMemo = { comments: state.comments, filters: state.filters, entries: buildEntries(state.comments, state.filters) };
  }
  return entriesMemo.entries;
}

const activeTier = (state) => state.tiers.find((t) => t.id === state.activeTierId);

/* ------------------------------------------------------------------ */
/* Views, engine, presentation                                         */
/* ------------------------------------------------------------------ */
// Phones can't capture their screen, so there the recording is a portrait recap
// redrawn on a canvas. The views feed it their frames; it never picks anyone.
const canvasMode = recorder.captureMode === 'canvas';
const recap = createCompositor({ wheelCanvas: $('#wheel-canvas') });
recorder.setCanvasSource(() => recap.canvas);
onCelebrate(recap.burst);
const slot = createSlotView({ reel: $('#slot-reel'), frame: $('.slot-window'), onFrame: recap.setStage });
const wheel = createWheelView({
  canvas: $('#wheel-canvas'),
  pointer: $('#wheel-pointer'),
  note: $('#wheel-note'),
  onFrame: recap.setStage,
});
const countdown = createCountdownView({
  root: $('#countdown-view'),
  ring: $('#cd-ring'),
  number: $('#cd-number'),
  name: $('#cd-name'),
  user: $('#cd-user'),
  onFrame: recap.setStage,
});
const presentation = createPresentation({
  onChange: onPresentationChange,
  beforeEnter: armBeforePresenting,
});
const participantScroll = createAutoScroll(ui.page.participantList);
const winnersScroll = createAutoScroll(ui.page.winnersList);
let revealed = null;
/** Present View phases: 'intro' (participants) → 'draw' (the stage) → 'winners' (results). */
let presentPhase = null;

const engine = createDrawEngine({
  store,
  getEntries,
  views: { slot, wheel, countdown },
  ui: {
    setBusy(busy) {
      ui.viewport.setAttribute('aria-busy', String(busy));
      renderDrawState(store.get());
      renderDashboard(store.get());
    },
    setAutoRun() {
      renderDrawState(store.get());
    },
    showReveal,
    hideReveal,
  },
});

/* ------------------------------------------------------------------ */
/* Renderers                                                           */
/* ------------------------------------------------------------------ */
function applyTheme(state) {
  document.documentElement.dataset.theme = state.theme;
  ui.themeSelect.value = state.theme;
  $('meta[name="theme-color"]')?.setAttribute('content', themeVar('--tk-bg'));
  wheel.refreshTheme();
  recap.refreshTheme();
}

function renderMute(state) {
  setMuted(state.muted);
  ui.muteBtn.setAttribute('aria-pressed', String(state.muted));
  ui.muteBtn.title = state.muted ? 'Unmute sound effects (M)' : 'Mute sound effects (M)';
  setIcon(ui.muteBtn.querySelector('[data-icon]'), state.muted ? 'mute' : 'volume');
}

function renderRecord() {
  ui.stopShareBtn.hidden = canvasMode || !recorder.isCaptureLive(); // nothing is shared in canvas mode
}

const SOURCE_LABELS = { tiktok: 'TikTok', rapidapi: 'RapidAPI', mock: 'Demo data' };

function renderLoadStatus(state) {
  const info = state.source;
  ui.loadStatus.classList.toggle('hidden', !info);
  if (!info) return;
  const count = state.comments.length;
  ui.loadStatus.replaceChildren(
    el('span', { class: 'chip', text: SOURCE_LABELS[info.source] ?? info.source }),
    info.videoId ? el('span', { class: 'chip chip-muted', text: `Video ${info.videoId}` }) : null,
    el('span', {
      class: 'text-muted',
      text: `${fmt(count)} comment${count === 1 ? '' : 's'}${info.total > count ? ` of ${fmt(info.total)}` : ''}`,
    }),
  );
}

function renderEntries(state) {
  const entries = getEntries(state);
  ui.stats.comments.textContent = fmt(state.comments.length);
  ui.stats.entries.textContent = fmt(entries.length);
  ui.stats.people.textContent = fmt(countPeople(entries));
  ui.stats.eligible.textContent = fmt(eligibleEntries(entries, state.winners).length);
}

/* Present View: the creator (page header + the strip above the stage), participants, winners. */
function renderCreator(state) {
  const { creator } = state;
  const profile = { avatar: creator.avatar, displayName: creator.displayName, username: creator.username };
  const title = creatorTitle(creator);
  const sub = creatorSubtitle(creator);
  ui.page.avatar.replaceChildren(avatarNode(profile));
  ui.page.title.textContent = title;
  ui.page.sub.textContent = sub;
  ui.page.sub.hidden = !sub;
  ui.stageCreator.avatar.replaceChildren(avatarNode(profile));
  ui.stageCreator.title.textContent = title;
}

function participantRow(entry, won) {
  return el('li', { class: `list-row${won ? ' is-winner' : ''}` }, [
    avatarNode(entry),
    el('p', { class: 'list-row-text' }, [
      el('span', { class: 'list-row-name', text: entry.displayName || entry.username }),
      won ? el('span', { class: 'chip list-row-won', text: '🏆 Won' }) : null,
      el('span', { text: entry.comment }),
    ]),
  ]);
}

/** Only rendered on the intro: thousands of rows are wasted DOM otherwise. */
function renderParticipants(state) {
  if (presentPhase !== 'intro') return;
  const entries = getEntries(state);
  const won = winnerKeys(state.winners);
  ui.page.participantsCount.textContent = fmt(entries.length);
  ui.page.participantList.replaceChildren(...entries.map((entry) => participantRow(entry, won.has(entry.key))));
  ui.page.participantEmpty.hidden = entries.length > 0;
  ui.page.participantEmpty.textContent = state.comments.length
    ? 'No comments match your filters.'
    : 'Load comments to see the participants here.';
}

function presentWinnerRow(winner, rank) {
  return el('li', { class: 'list-row' }, [
    el('span', { class: 'list-rank', text: String(rank) }),
    avatarNode(winner),
    el('div', { class: 'list-row-main' }, [
      el('p', { class: 'list-row-title' }, [
        el('span', { class: 'list-row-name', text: winner.displayName || winner.username }),
        el('span', { class: 'list-row-handle', text: `@${winner.username}` }),
      ]),
      el('p', { class: 'list-row-comment', text: winner.comment }),
    ]),
  ]);
}

/** The results page: valid winners (voided ones left out), grouped by tier in draw order. */
function renderPresentWinners(state) {
  if (presentPhase !== 'winners') return;
  const active = state.winners.filter((w) => !w.voided);
  const rows = [];
  for (const tier of state.tiers) {
    const winners = active.filter((w) => w.tierId === tier.id);
    if (!winners.length) continue;
    rows.push(
      el('li', { class: 'list-group' }, [
        el('span', { text: tier.name || 'Untitled tier' }),
        tier.prize ? el('span', { class: 'list-group-prize', text: tier.prize }) : null,
      ]),
      ...winners.map((w, i) => presentWinnerRow(w, i + 1)),
    );
  }
  ui.page.winnersCount.textContent = fmt(active.length);
  ui.page.winnersList.replaceChildren(...rows);
}

function updateTier(id, patch, meta) {
  store.set((s) => ({ tiers: s.tiers.map((t) => (t.id === id ? { ...t, ...patch } : t)) }), meta);
}

function renderTierEditor(state) {
  const rows = state.tiers.map((tier, index) => {
    const filled = tierFilled(tier, state.winners);
    const hasAnyWinner = state.winners.some((w) => w.tierId === tier.id);
    const nameId = `tier-name-${tier.id}`;
    const prizeId = `tier-prize-${tier.id}`;
    const qtyId = `tier-qty-${tier.id}`;

    const qty = el('input', {
      class: 'input text-center',
      attrs: { id: qtyId, type: 'number', min: minQuantity(tier, state.winners), max: MAX_QUANTITY, value: tier.quantity, inputmode: 'numeric' },
      on: {
        change: (e) => {
          const value = clampQuantity(e.target.value, tier, store.get().winners);
          e.target.value = value;
          updateTier(tier.id, { quantity: value }, { source: 'tier-editor' });
        },
      },
    });

    const toolButton = (name, label, onClick, disabled = false) =>
      el('button', {
        class: 'btn btn-ghost btn-sm btn-icon',
        attrs: { type: 'button', title: label, 'aria-label': label, disabled },
        on: { click: onClick },
      }, [icon(name)]);

    return el('li', { class: 'tier-row' }, [
      el('div', { class: 'tier-tools' }, [
        el('span', { class: 'flex min-w-0 items-baseline gap-2' }, [
          el('span', { class: 'panel-label mb-0', text: `Tier ${index + 1}` }),
          el('span', { class: 'truncate text-xs text-muted', text: `${filled} / ${tier.quantity} drawn` }),
        ]),
        el('span', { class: 'flex gap-1.5' }, [
          toolButton('up', 'Move up', () => store.set((s) => ({ tiers: moveTier(s.tiers, tier.id, -1) })), index === 0),
          toolButton('down', 'Move down', () => store.set((s) => ({ tiers: moveTier(s.tiers, tier.id, 1) })), index === state.tiers.length - 1),
          toolButton(
            'trash',
            hasAnyWinner ? 'Tiers with winners can’t be removed' : 'Remove tier',
            () => removeTier(tier.id),
            hasAnyWinner,
          ),
        ]),
      ]),
      el('div', {}, [
        el('label', { class: 'field-label text-xs', text: 'Tier name', attrs: { for: nameId } }),
        el('input', {
          class: 'input',
          attrs: { id: nameId, type: 'text', value: tier.name, maxlength: 60 },
          on: { input: (e) => updateTier(tier.id, { name: e.target.value }, { source: 'tier-editor' }) },
        }),
      ]),
      el('div', {}, [el('label', { class: 'field-label text-xs', text: 'Winners', attrs: { for: qtyId } }), qty]),
      el('div', { class: 'col-span-2' }, [
        el('label', { class: 'field-label text-xs', text: 'Prize', attrs: { for: prizeId } }),
        el('input', {
          class: 'input',
          attrs: { id: prizeId, type: 'text', value: tier.prize, maxlength: 80, placeholder: 'What do they win?' },
          on: { input: (e) => updateTier(tier.id, { prize: e.target.value }, { source: 'tier-editor' }) },
        }),
      ]),
    ]);
  });
  ui.tierEditor.replaceChildren(...rows);
}

function removeTier(id) {
  store.set((s) => {
    if (s.winners.some((w) => w.tierId === id)) return {};
    const tiers = s.tiers.filter((t) => t.id !== id);
    const activeTierId = s.activeTierId === id ? (nextOpenTierId(tiers, s.winners, tiers[0]?.id) ?? tiers[0]?.id ?? null) : s.activeTierId;
    return { tiers, activeTierId };
  });
}

function renderDashboard(state) {
  const busy = engine.isBusy();
  if (!state.tiers.length) {
    ui.dashboard.replaceChildren(el('p', { class: 'empty-state col-span-full', text: 'Add a prize tier on the previous page.' }));
    return;
  }
  ui.dashboard.replaceChildren(
    ...state.tiers.map((tier) => {
      const filled = tierFilled(tier, state.winners);
      const full = filled >= tier.quantity;
      const pct = Math.min(100, Math.round((filled / tier.quantity) * 100));
      return el('button', {
        class: `tier-card${full ? ' is-full' : ''}`,
        attrs: { type: 'button', role: 'radio', 'aria-checked': String(tier.id === state.activeTierId), disabled: busy },
        on: { click: () => store.set({ activeTierId: tier.id }) },
      }, [
        el('p', { class: 'tier-card-name', text: tier.name || 'Untitled tier' }),
        el('p', { class: 'tier-card-prize', text: full ? '✓ Complete' : tier.prize || '—' }),
        el('span', { class: 'tier-card-count', text: `${filled}/${tier.quantity}` }),
        el('div', { class: 'progress' }, [el('span', { attrs: { style: `width:${pct}%` } })]),
      ]);
    }),
  );
}

function renderNowDrawing(state) {
  const tier = activeTier(state);
  if (!tier) {
    ui.nowTier.textContent = '—';
    ui.nowPrize.textContent = '';
    return;
  }
  ui.nowTier.textContent = tier.name || 'Untitled tier';
  ui.nowPrize.textContent = nowDrawingDetail(tier, state);
}

function nowDrawingDetail(tier, state) {
  const left = Math.max(0, tier.quantity - tierFilled(tier, state.winners));
  return [tier.prize, left ? `${left} of ${tier.quantity} left` : 'complete'].filter(Boolean).join(' · ');
}

function renderControls(state) {
  ui.viewport.dataset.mode = state.mode;
  for (const button of ui.modeButtons) button.setAttribute('aria-checked', String(button.dataset.mode === state.mode));
  ui.durationInput.value = String(state.duration);
  // A countdown runs in whole seconds.
  ui.durationOutput.textContent =
    state.mode === 'countdown' ? `${countdownSeconds(state.duration * 1000)} s` : `${state.duration.toFixed(1)} s`;
}

function renderDrawState(state) {
  const busy = engine.isBusy();
  const auto = engine.isAutoRunning();
  const status = drawStatus(state, getEntries(state));
  ui.drawBtn.disabled = busy || !status.ok;
  const presenting = presentation.isOn();
  // Outside Present View the button opens it (see triggerDraw), so it says so.
  // A click (or Space) during the pause between draws stops the run, so say so.
  const opens = state.winners.length ? 'Continue giveaway' : 'Start giveaway';
  ui.drawBtnLabel.textContent = busy ? 'Drawing…' : auto ? 'Stop after this draw' : presenting ? 'Draw winner' : opens;
  ui.drawBtn.title = presenting ? '' : 'Opens Present View (fullscreen). Press Start! there to run the first draw.';
  ui.drawHint.textContent = status.reason;
  // In Present View, a finished giveaway swaps Draw for the way to the results page.
  const finished = presenting && !busy && isGiveawayComplete(state, getEntries(state));
  ui.drawBtn.hidden = finished;
  ui.viewWinnersBtn.hidden = !finished;
  for (const button of ui.modeButtons) button.disabled = busy;
  ui.loadBtn.disabled = busy || loading;
  ui.demoBtn.disabled = busy || loading;
}

/* ------------------------------------------------------------------ */
/* Steps: link → entries & prizes → draw → results (lib/steps.js)      */
/* ------------------------------------------------------------------ */
let currentStep = null;

function showStep(requested, { focus = true } = {}) {
  const step = resolveStep(requested, { hasComments: store.get().comments.length > 0 });
  if (location.hash !== stepHash(step)) history.replaceState(null, '', stepHash(step));
  if (step === currentStep) return;
  currentStep = step;
  document.body.dataset.step = step;
  for (const page of ui.steps.pages) page.hidden = page.dataset.stepPage !== step;
  renderStepNav(store.get());
  window.dispatchEvent(new Event('resize')); // the wheel measures its canvas once it is visible
  if (!focus) return;
  scrollTo({ top: 0 });
  $(`[data-step-page="${step}"] h2[tabindex]`)?.focus({ preventScroll: true });
}

/** Adds a history entry, so the browser's Back button returns to the previous step. */
function setStep(step) {
  if (location.hash !== stepHash(step)) location.hash = stepHash(step);
  showStep(step);
}

function renderStepNav(state) {
  const hasComments = state.comments.length > 0;
  for (const link of ui.steps.links) {
    const step = link.dataset.stepLink;
    link.disabled = resolveStep(step, { hasComments }) !== step;
    if (step === currentStep) link.setAttribute('aria-current', 'step');
    else link.removeAttribute('aria-current');
  }
  ui.steps.continueVideoBtn.hidden = !hasComments;

  const entries = getEntries(state);
  const status = drawStatus(state, entries);
  ui.steps.setupNextBtn.disabled = !entries.length || !state.tiers.length;
  ui.steps.setupHint.textContent = status.reason;
  ui.steps.drawResultsBtn.hidden = !state.winners.length;

  const total = state.tiers.reduce((sum, t) => sum + t.quantity, 0);
  const drawn = state.tiers.reduce((sum, t) => sum + Math.min(t.quantity, tierFilled(t, state.winners)), 0);
  const left = hasComments && !isGiveawayComplete(state, entries) ? ' To draw the rest, go back to step 3, Draw.' : '';
  ui.steps.resultsSummary.textContent = `${fmt(drawn)} of ${fmt(total)} prize${total === 1 ? '' : 's'} drawn.${left}`;
}

function renderStageIdle(state) {
  const status = drawStatus(state, getEntries(state));
  if (state.mode === 'wheel') return wheel.setPool(status.pool);
  const [title, subtitle] = status.ok ? ['Ready to draw ✨', `${fmt(status.pool.length)} in the pool`] : [status.reason, ''];
  if (state.mode === 'countdown') countdown.showMessage(title, subtitle, countdownSeconds(state.duration * 1000));
  else slot.showMessage(title, subtitle);
}

function renderWinners(state, meta = {}) {
  const groups = state.tiers
    .map((tier) => ({ tier, winners: state.winners.filter((w) => w.tierId === tier.id) }))
    .filter((group) => group.winners.length);

  ui.winnersList.replaceChildren(
    ...groups.map(({ tier, winners }) =>
      el('section', { class: 'panel' }, [
        el('div', { class: 'panel-head winner-group-head' }, [
          el('span', { class: 'flex min-w-0 flex-wrap items-baseline gap-x-2 gap-y-0.5' }, [
            el('h3', { class: 'panel-label max-w-full truncate', text: tier.name || 'Untitled tier' }),
            tier.prize ? el('span', { class: 'max-w-full truncate text-xs text-muted', text: tier.prize }) : null,
          ]),
          el('span', { class: 'shrink-0 text-xs whitespace-nowrap text-muted tabular-nums', text: `${activeWinners(tier.id, winners).length} / ${tier.quantity}` }),
        ]),
        el('ol', {}, rankWinners(winners).map(([w, rank]) => winnerRow(w, rank, meta.newWinnerId === w.id))),
      ]),
    ),
  );
  ui.winnersEmpty.hidden = state.winners.length > 0;
  ui.exportCsvBtn.disabled = !state.winners.length;
  ui.clearWinnersBtn.disabled = !state.winners.length;
}

/** Voided winners don't take a rank number. */
function rankWinners(winners) {
  let rank = 0;
  return winners.map((w) => [w, w.voided ? '✕' : String(++rank)]);
}

function winnerRow(w, rank, isNew) {
  const actions = w.voided
    ? [el('span', { class: 'chip chip-danger', text: 'Voided' })]
    : [
        el('button', {
          class: 'btn btn-ghost btn-sm btn-icon',
          attrs: { type: 'button', title: 'Download certificate (.png)', 'aria-label': `Download certificate for ${w.displayName}` },
          on: { click: () => exportCertificate(w) },
        }, [icon('image')]),
        el('button', {
          class: 'btn btn-ghost btn-sm btn-icon btn-danger hide-in-presenting',
          attrs: { type: 'button', title: 'Void & redraw', 'aria-label': `Void ${w.displayName}` },
          on: { click: () => voidWinner(w.id) },
        }, [icon('ban')]),
      ];

  return el('li', { class: `winner-row${w.voided ? ' is-voided' : ''}${isNew ? ' is-new' : ''}`, dataset: { winnerId: w.id } }, [
    el('span', { class: 'winner-rank', text: String(rank) }),
    el('div', { class: 'winner-body min-w-0 flex-1' }, [
      el('p', { class: 'winner-name', text: w.displayName }),
      el('p', { class: 'truncate text-xs text-accent-2', text: `@${w.username}` }),
      el('p', { class: 'entry-comment text-muted', text: w.comment }),
    ]),
    el('div', { class: 'winner-actions' }, actions),
  ]);
}

/* ------------------------------------------------------------------ */
/* Reveal                                                              */
/* ------------------------------------------------------------------ */
function showReveal(record) {
  revealed = record;
  $('#reveal-tier').textContent = [record.tierName, record.prize].filter(Boolean).join(' — ');
  $('#reveal-name').textContent = record.displayName;
  $('#reveal-username').textContent = `@${record.username}`;
  $('#reveal-comment').textContent = record.comment;
  ui.reveal.hidden = false;
  recap.setReveal(record);
}

function hideReveal() {
  revealed = null;
  ui.reveal.hidden = true;
  recap.setReveal(null);
}

/* ------------------------------------------------------------------ */
/* Actions                                                             */
/* ------------------------------------------------------------------ */
let loading = false;
let loadController = null;

function setLoading(on) {
  loading = on;
  ui.loadBtn.replaceChildren(...(on ? [el('span', { class: 'spinner' }), el('span', { text: 'Loading' })] : [icon('download'), el('span', { text: 'Load' })]));
  if (on) {
    ui.loadStatus.classList.remove('hidden');
    ui.loadStatus.replaceChildren(
      el('span', { class: 'spinner text-accent' }),
      el('span', { class: 'text-muted', text: 'Fetching comments… videos with thousands of comments can take up to a minute.' }),
    );
  } else {
    renderLoadStatus(store.get());
  }
  renderDrawState(store.get());
}

async function fetchComments({ url, demo = false }) {
  if (engine.isBusy()) return;
  loadController?.abort();
  loadController = new AbortController();
  setLoading(true);
  try {
    const result = await loadComments({ url, demo, signal: loadController.signal });
    store.set((s) => ({
      comments: result.comments,
      videoUrl: url ?? '',
      source: { source: result.source, videoId: result.videoId, total: result.total, fetchedAt: new Date().toISOString() },
      creator: resolveCreator(s.creator, { url, comments: result.comments, fetched: result.creator, demo }),
    }));
    if (result.comments.length) {
      toast(`Loaded ${fmt(result.comments.length)} comments${demo ? ' (demo data)' : ''}.`, { type: 'success' });
      setStep('setup');
    } else {
      toast('No comments found on that video (or comments are turned off).', { type: 'error' });
    }
  } catch (err) {
    if (err.name === 'AbortError') return;
    toast(err instanceof ApiError ? err.message : `Could not load comments: ${err.message}`, { type: 'error' });
  } finally {
    setLoading(false);
  }
}

async function exportCertificate(winner) {
  try {
    const state = store.get();
    await downloadCertificate(winner, { videoId: state.source?.videoId, host: creatorTitle(state.creator) });
  } catch (err) {
    toast(`Could not create the certificate: ${err.message}`, { type: 'error' });
  }
}

function voidWinner(id) {
  if (engine.isBusy()) return;
  const state = store.get();
  const winner = state.winners.find((w) => w.id === id);
  if (!winner || winner.voided) return;
  const ok = confirm(
    `Void @${winner.username} for “${winner.tierName}”?\n\nThey stay excluded from all future draws and the slot reopens so you can draw again.`,
  );
  if (!ok) return;
  store.set({
    winners: state.winners.map((w) => (w.id === id ? { ...w, voided: true, voidedAt: new Date().toISOString() } : w)),
    activeTierId: state.tiers.some((t) => t.id === winner.tierId) ? winner.tierId : state.activeTierId,
  });
  if (revealed?.id === id) hideReveal();
  toast(`@${winner.username} voided — draw again to fill the slot.`);
}

/** Recordings the host has not downloaded yet; they only exist in this tab's memory. */
const unsavedRecordings = new Set();

function addRecording({ blob, durationMs, extension, label, isMp4 }) {
  const state = store.get();
  const filename = `tokdraw-${slug(state.creator.username, 'giveaway')}-${timestamp()}.${extension}`;
  const drawn = state.winners.filter((w) => !w.voided).length;
  const url = URL.createObjectURL(blob);
  // Until it's downloaded the file only lives in this tab, so say so and make Download the main action.
  const unsaved = el('span', { class: 'chip chip-danger shrink-0', text: 'Not downloaded' });
  const link = el('a', { class: 'btn btn-primary btn-sm', attrs: { href: url, download: filename } }, [icon('download'), el('span', { class: 'recording-dl-label', text: 'Download' })]);
  link.addEventListener('click', () => {
    unsavedRecordings.delete(link);
    unsaved.remove();
    link.classList.replace('btn-primary', 'btn-ghost');
  });
  ui.recordingsEmpty.hidden = true;
  ui.recordingsList.prepend(
    el('li', { class: 'recording-row' }, [
      el('span', { class: 'recording-icon', text: '🎬', attrs: { 'aria-hidden': 'true' } }),
      el('div', { class: 'min-w-0 flex-1' }, [
        el('p', { class: 'truncate text-sm font-semibold', text: `Giveaway session · ${drawn} winner${drawn === 1 ? '' : 's'}` }),
        el('div', { class: 'mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-1' }, [
          el('span', { class: 'text-xs text-muted tabular-nums', text: `${clock(durationMs)} · ${(blob.size / 1048576).toFixed(1)} MB · ${label}` }),
          unsaved,
        ]),
        el('p', { class: 'recording-file', text: filename, attrs: { title: filename } }),
      ]),
      link,
    ]),
  );
  unsavedRecordings.add(link);
  const head = `Recording ready: ${filename}. Download it from the Recordings list`;
  toast(isMp4 ? head : `${head}. This browser can’t record MP4 — use Chrome/Edge 126+ or Safari to get an .mp4.`, { type: 'success' });
}

/** Text the recap canvas must have fonts for: the creator, tiers and a slice of the names. */
function recapFontSample() {
  const state = store.get();
  const names = getEntries(state).slice(0, 200).map((e) => `${e.displayName} ${e.comment}`);
  return [state.creator.displayName, ...state.tiers.map((t) => `${t.name} ${t.prize}`), ...names].join(' ');
}

/** Opens the capture (the browser's picker on desktop) and arms the next Present View. Throws if refused. */
async function armRecording() {
  unlockAudio();
  if (canvasMode) await recap.prepare(recapFontSample());
  await recorder.armCapture();
  store.set({ record: true });
  if (canvasMode) {
    updateRecap(store.get());
    recap.render();
  }
  renderRecord(store.get());
}

let armingBeforePresent = false;

/**
 * Every Present View is recorded: the click that opens it also asks for the capture,
 * so the picker appears before fullscreen and not on top of the show. Resolves to
 * whether Present View may open.
 */
async function armBeforePresenting() {
  if (store.get().record || !recorder.recordingSupported) return true;
  if (armingBeforePresent) return false; // a second click while the picker is open
  armingBeforePresent = true;
  try {
    await armRecording();
    return true;
  } catch (err) {
    if (err?.name !== 'NotAllowedError') toast(err.message, { type: 'error' });
    return confirm('Recording was not started, so this draw will not be saved as a video.\n\nPresent without recording?');
  } finally {
    armingBeforePresent = false;
  }
}

function triggerDraw() {
  if (presentPhase === 'intro') return startFromIntro();
  if (presentPhase === 'winners') return;
  if (!ui.viewWinnersBtn.hidden) return showWinnersPage();
  if (engine.isAutoRunning()) return engine.stopAutoRun();
  if (ui.drawBtn.disabled) return;
  // Recording only runs inside Present View, so the page-level Draw opens the show
  // (the intro, then Start!) instead of drawing where nothing would be recorded.
  if (!presentation.isOn()) return presentation.enter();
  engine.drawOne();
}

/* ------------------------------------------------------------------ */
/* Present View: intro (participants) → draw → winners (results)      */
/* ------------------------------------------------------------------ */
// The tap that opens Present View lands where Start! appears, so a double tap would
// skip the intro (and its part of the recording). Start! ignores taps until it has slid in.
const START_GUARD_MS = 700;
let introShownAt = 0;

function setPresentPhase(phase) {
  presentPhase = phase;
  if (phase === 'intro') introShownAt = performance.now();
  if (phase) document.body.dataset.presentPhase = phase;
  else delete document.body.dataset.presentPhase;
  if (phase === 'intro') {
    renderParticipants(store.get());
    participantScroll.reset();
    participantScroll.start();
  } else {
    participantScroll.stop();
    ui.page.participantList.replaceChildren();
  }
  if (phase === 'winners') {
    renderPresentWinners(store.get());
    winnersScroll.reset();
    winnersScroll.start();
  } else {
    winnersScroll.stop();
  }
  renderDrawState(store.get());
  window.dispatchEvent(new Event('resize')); // let canvases re-measure
  if (canvasMode && store.get().record) {
    const state = store.get();
    updateRecap(state);
    recap.setPhase(phase);
    if (phase === 'intro') recap.setIntro(introScene(state, getEntries(state)));
    if (phase === 'winners') recap.setWinners(winnersScene(state));
  }
}

function onPresentationChange(on) {
  if (on) {
    unlockAudio(); // still inside the click / F key gesture
    setStep('draw'); // Present View runs on the draw page's stage
    setPresentPhase(engine.isBusy() ? 'draw' : 'intro');
    drewThisShow = false;
    scheduleSessionRecording();
  } else {
    setPresentPhase(null);
    hideReveal();
    // A finished giveaway needs no more recording, so don't leave the tab shared.
    // With prizes left, the share stays live so the next show starts without the picker.
    const state = store.get();
    if (isGiveawayComplete(state, getEntries(state))) endShare();
    else stopSessionRecording();
    setStep('results'); // the winners and the session's recording
  }
}

function startFromIntro() {
  if (performance.now() - introShownAt < START_GUARD_MS) return;
  setPresentPhase('draw');
  ui.page.startBtn.blur();
  if (!ui.drawBtn.disabled) engine.drawOne();
}

function showWinnersPage() {
  hideReveal();
  setPresentPhase('winners');
  celebrate();
  if (recorder.isRecording()) {
    clearTimeout(recordStopTimer);
    recordStopTimer = setTimeout(stopSessionRecording, RECORD_WINNERS_TAIL_MS);
  }
}

/* ------------------------------------------------------------------ */
/* Session recording: from entering Present View to the winners page   */
/* ------------------------------------------------------------------ */
/** Pushes the creator and the prize being drawn to the recap video (canvas mode only). */
function updateRecap(state) {
  const { creator } = state;
  recap.setCreator({ title: creatorTitle(creator), sub: creatorSubtitle(creator), avatar: creator.avatar });
  const tier = activeTier(state);
  recap.setTier(tier ? { name: tier.name || 'Untitled tier', detail: nowDrawingDetail(tier, state) } : null);
}

const RECORD_START_DELAY_MS = 700; // let fullscreen and the intro layout settle first
const RECORD_WINNERS_TAIL_MS = 10_000; // the results page closes the video
const RECORD_FINAL_TAIL_MS = 60_000; // fallback if the host never opens the results page
let recordStartTimer = 0;
let recordStopTimer = 0;
let drewThisShow = false; // a show left before any draw records nothing worth keeping

function scheduleSessionRecording() {
  clearTimeout(recordStartTimer);
  if (store.get().record) recordStartTimer = setTimeout(startSessionRecording, RECORD_START_DELAY_MS);
}

function startSessionRecording() {
  if (!presentation.isOn() || !store.get().record || recorder.isRecording()) return;
  try {
    recorder.startRecording(recordingAudioTrack());
    if (canvasMode) recap.start();
  } catch (err) {
    toast(`Recording could not start: ${err.message}`, { type: 'error' });
    return;
  }
  renderRecord(store.get());
}

async function stopSessionRecording() {
  clearTimeout(recordStartTimer);
  clearTimeout(recordStopTimer);
  if (!recorder.isRecording()) return;
  ui.recordingSaving.hidden = false;
  let result;
  try {
    result = await recorder.stopRecording();
  } finally {
    ui.recordingSaving.hidden = true;
  }
  recap.stop();
  renderRecord(store.get());
  if (!drewThisShow) toast('No winner was drawn, so the recording was discarded.');
  else if (result) addRecording(result);
  else toast('The recording came out empty, so nothing was saved.', { type: 'error' });
}

function afterWinner(state) {
  drewThisShow = true;
  if (recorder.isRecording() && isGiveawayComplete(state, getEntries(state))) {
    clearTimeout(recordStopTimer);
    recordStopTimer = setTimeout(stopSessionRecording, RECORD_FINAL_TAIL_MS);
  }
}

function syncInputs(state) {
  ui.urlInput.value = state.videoUrl;
  ui.keywordInput.value = state.filters.keywords;
  ui.excludeInput.value = state.filters.exclude;
  ui.uniqueInput.checked = state.filters.uniqueOnly;
}

/* ------------------------------------------------------------------ */
/* Events                                                              */
/* ------------------------------------------------------------------ */
const debounce = (fn, ms) => {
  let timer = 0;
  return (...args) => {
    clearTimeout(timer);
    timer = setTimeout(() => fn(...args), ms);
  };
};
const updateFilters = (patch) => store.set((s) => ({ filters: { ...s.filters, ...patch } }), { source: 'filters' });

ui.themeSelect.addEventListener('change', () => store.set({ theme: ui.themeSelect.value }));
ui.muteBtn.addEventListener('click', () => {
  unlockAudio();
  store.set((s) => ({ muted: !s.muted }));
});
/** Saves any running recording, then ends the tab share; the next Present View asks again. */
async function endShare() {
  await stopSessionRecording();
  recorder.releaseCapture();
  store.set({ record: false });
  renderRecord(store.get());
}
ui.stopShareBtn.addEventListener('click', endShare);
recorder.onCaptureEnded(() => {
  stopSessionRecording(); // keep what was captured so far
  if (store.get().record) {
    store.set({ record: false });
    toast('Screen sharing stopped — recording turned off.');
  }
  renderRecord(store.get());
});
recorder.onRecordingInterrupted(() => {
  toast('The recorder stopped unexpectedly — saving what was captured.', { type: 'error' });
  stopSessionRecording();
});
recorder.onRecordingBackgrounded((ms) => {
  toast(`TokDraw was in the background for ${Math.round(ms / 1000)} s, so the video froze for that long. Keep this page open while recording.`, {
    type: 'error',
  });
});
addEventListener('beforeunload', (e) => {
  if (recorder.isRecording() || unsavedRecordings.size) e.preventDefault(); // recordings only exist in memory
});

ui.loadForm.addEventListener('submit', (e) => {
  e.preventDefault();
  const url = ui.urlInput.value.trim();
  if (!url) {
    toast('Paste a TikTok video link first — or try the demo data.');
    ui.urlInput.focus();
    return;
  }
  fetchComments({ url });
});
ui.demoBtn.addEventListener('click', () => fetchComments({ url: ui.urlInput.value.trim() || undefined, demo: true }));

addEventListener('hashchange', () => {
  // Present View sits on the draw page; Back mid-show would hide the stage.
  if (presentation.isOn()) return history.replaceState(null, '', stepHash('draw'));
  showStep(parseStep(location.hash));
});
for (const button of [...ui.steps.links, ...$$('[data-go-step]')]) {
  button.addEventListener('click', () => setStep(button.dataset.stepLink ?? button.dataset.goStep));
}
ui.steps.continueVideoBtn.addEventListener('click', () => setStep('setup'));

ui.keywordInput.addEventListener('input', debounce(() => updateFilters({ keywords: ui.keywordInput.value }), 150));
ui.excludeInput.addEventListener('input', debounce(() => updateFilters({ exclude: ui.excludeInput.value }), 200));
ui.uniqueInput.addEventListener('change', () => updateFilters({ uniqueOnly: ui.uniqueInput.checked }));

ui.addTierBtn.addEventListener('click', () => {
  const tier = createTier(`Tier ${store.get().tiers.length + 1}`, '', 1);
  store.set((s) => ({ tiers: [...s.tiers, tier], activeTierId: s.tiers.length ? s.activeTierId : tier.id }));
  $(`#tier-name-${tier.id}`)?.select();
});

for (const button of ui.modeButtons) {
  button.addEventListener('click', () => store.set({ mode: button.dataset.mode }));
}
ui.durationInput.addEventListener('input', () => store.set({ duration: Number(ui.durationInput.value) }));

ui.drawBtn.addEventListener('click', triggerDraw);
ui.page.startBtn.addEventListener('click', startFromIntro);
ui.viewWinnersBtn.addEventListener('click', showWinnersPage);

$('#reveal-close').addEventListener('click', hideReveal);

ui.exportCsvBtn.addEventListener('click', () => {
  const csv = winnersToCsv(store.get().winners);
  downloadBlob(new Blob([csv], { type: 'text/csv;charset=utf-8' }), `tokdraw-winners-${timestamp()}.csv`);
});
ui.clearWinnersBtn.addEventListener('click', () => {
  if (engine.isBusy()) return;
  if (!confirm('Clear all winners? Everyone becomes eligible again. Comments and prize tiers are kept.')) return;
  store.set((s) => ({ winners: [], activeTierId: s.tiers[0]?.id ?? null }));
  hideReveal();
});
function resetAll() {
  if (engine.isBusy()) return;
  const unsaved = unsavedRecordings.size
    ? `\n\n${unsavedRecordings.size} recording${unsavedRecordings.size === 1 ? ' is' : 's are'} not downloaded yet and will be lost if you close this page.`
    : '';
  if (!confirm(`Start a new giveaway? This clears comments, filters, prize tiers and winners.${unsaved}`)) return;
  hideReveal();
  store.reset();
  setStep('video');
}
ui.resetAllBtn.addEventListener('click', resetAll);
ui.steps.newGiveawayBtn.addEventListener('click', resetAll);

ui.exitPresentBtn.addEventListener('click', () => presentation.exit());

document.addEventListener('keydown', (e) => {
  if (e.defaultPrevented || e.ctrlKey || e.metaKey || e.altKey) return;
  const target = e.target;
  const typing = ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName) || target.isContentEditable;
  if (typing) return;
  const onControl = ['BUTTON', 'A'].includes(target.tagName);
  switch (e.key) {
    case ' ':
    case 'Enter':
      if (onControl) return;
      e.preventDefault();
      if (e.repeat) return; // a held key would otherwise skip the intro and start the first draw
      if (presentation.isOn() || currentStep === 'draw') triggerDraw();
      break;
    case 'f':
    case 'F':
      if (presentation.isOn() || currentStep === 'draw' || currentStep === 'results') presentation.toggle();
      break;
    case 'm':
    case 'M':
      unlockAudio();
      store.set((s) => ({ muted: !s.muted }));
      break;
    case 'Escape':
      if (!ui.reveal.hidden) hideReveal();
      else if (presentation.isOn()) presentation.exit();
      break;
  }
});

/* ------------------------------------------------------------------ */
/* Store → UI                                                          */
/* ------------------------------------------------------------------ */
const any = (set, ...keys) => keys.some((k) => set.has(k));

store.subscribe((state, meta) => {
  const c = meta.changed;
  if (c.has('theme')) applyTheme(state);
  if (c.has('muted')) renderMute(state);
  if (c.has('record')) renderRecord(state);
  if (any(c, 'source', 'comments')) renderLoadStatus(state);
  if (any(c, 'creator', 'source')) renderCreator(state);
  if (any(c, 'comments', 'filters', 'winners')) {
    renderEntries(state);
    renderParticipants(state);
  }
  if (any(c, 'tiers', 'winners') && meta.source !== 'tier-editor') renderTierEditor(state);
  if (any(c, 'tiers', 'winners', 'activeTierId')) {
    renderDashboard(state);
    renderNowDrawing(state);
  }
  if (any(c, 'winners', 'tiers')) {
    renderWinners(state, meta);
    renderPresentWinners(state);
  }
  if (any(c, 'mode', 'duration')) renderControls(state);
  renderDrawState(state);
  if (c.has('comments')) showStep(currentStep, { focus: false }); // re-check the step guard
  if (any(c, 'comments', 'filters', 'winners', 'tiers', 'activeTierId')) renderStepNav(state);
  const idleKeys = state.mode === 'countdown' ? ['duration'] : []; // the dial shows the seconds
  if (any(c, 'comments', 'filters', 'winners', 'tiers', 'activeTierId', 'mode', ...idleKeys) && meta.source !== 'draw' && !engine.isBusy()) {
    renderStageIdle(state);
  }
  if (meta.source === 'reset') syncInputs(state);
  if (meta.source === 'draw') afterWinner(state);
  if (canvasMode && state.record && any(c, 'creator', 'tiers', 'winners', 'activeTierId')) updateRecap(state);
});

/* ------------------------------------------------------------------ */
/* Initial paint                                                       */
/* ------------------------------------------------------------------ */
(function init() {
  const state = store.get();
  applyTheme(state);
  renderMute(state);
  renderRecord(state);
  syncInputs(state);
  renderLoadStatus(state);
  renderCreator(state);
  renderEntries(state);
  renderTierEditor(state);
  renderDashboard(state);
  renderNowDrawing(state);
  renderWinners(state);
  renderControls(state);
  renderDrawState(state);
  showStep(parseStep(location.hash), { focus: false });
  renderStageIdle(state);
})();
